import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { SCENES } from "./scenes";
import type { CinematicState, Scene } from "./types";

export interface SavedDemoPreset {
  id: string;
  label: string;
  scenes: Scene[];
  createdAt: string;
}

interface CinematicContextValue {
  state: CinematicState;
  currentScene: Scene;
  scenes: Scene[];
  startCinematic: () => void;
  stopCinematic: () => void;
  nextScene: () => void;
  prevScene: () => void;
  goToScene: (index: number) => void;
  replayScene: () => void;
  toggleNarrator: () => void;
  markSceneComplete: (sceneId: string) => void;
  updateScene: (index: number, updates: Partial<Scene>) => void;
  resetScenes: () => void;
  savedPresets: SavedDemoPreset[];
  savePreset: (label: string) => void;
  loadPreset: (id: string) => void;
  deletePreset: (id: string) => void;
  activePresetId: string | null;
}

const CinematicContext = createContext<CinematicContextValue | null>(null);

const STORAGE_KEY = "koredor_demo_presets";

const INITIAL_STATE: CinematicState = {
  active: false,
  currentSceneIndex: 0,
  narratorOpen: true,
  softFocusEnabled: true,
  completedScenes: {},
};

function loadPresetsFromStorage(): SavedDemoPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistPresets(presets: SavedDemoPreset[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
}

export function CinematicProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CinematicState>(INITIAL_STATE);
  const [editableScenes, setEditableScenes] = useState<Scene[]>([...SCENES]);
  const [savedPresets, setSavedPresets] = useState<SavedDemoPreset[]>(loadPresetsFromStorage);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);

  useEffect(() => {
    persistPresets(savedPresets);
  }, [savedPresets]);

  const startCinematic = useCallback(() => {
    setState(prev => ({ ...prev, active: true, currentSceneIndex: 0, completedScenes: {} }));
  }, []);

  const stopCinematic = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const nextScene = useCallback(() => {
    setState(prev => {
      const next = Math.min(prev.currentSceneIndex + 1, editableScenes.length - 1);
      return { ...prev, currentSceneIndex: next };
    });
  }, [editableScenes.length]);

  const prevScene = useCallback(() => {
    setState(prev => {
      const next = Math.max(prev.currentSceneIndex - 1, 0);
      return { ...prev, currentSceneIndex: next };
    });
  }, []);

  const goToScene = useCallback((index: number) => {
    setState(prev => ({ ...prev, currentSceneIndex: Math.max(0, Math.min(index, editableScenes.length - 1)) }));
  }, [editableScenes.length]);

  const replayScene = useCallback(() => {
    setState(prev => {
      const sceneId = editableScenes[prev.currentSceneIndex]?.id;
      const completedScenes = { ...prev.completedScenes };
      if (sceneId) delete completedScenes[sceneId];
      return { ...prev, completedScenes };
    });
  }, [editableScenes]);

  const updateScene = useCallback((index: number, updates: Partial<Scene>) => {
    setEditableScenes(prev => prev.map((s, i) => i === index ? { ...s, ...updates } : s));
    setActivePresetId(null);
  }, []);

  const resetScenes = useCallback(() => {
    setEditableScenes([...SCENES]);
    setActivePresetId(null);
  }, []);

  const toggleNarrator = useCallback(() => {
    setState(prev => ({ ...prev, narratorOpen: !prev.narratorOpen }));
  }, []);

  const markSceneComplete = useCallback((sceneId: string) => {
    setState(prev => ({ ...prev, completedScenes: { ...prev.completedScenes, [sceneId]: true } }));
  }, []);

  const savePreset = useCallback((label: string) => {
    const preset: SavedDemoPreset = {
      id: crypto.randomUUID(),
      label,
      scenes: editableScenes.map(s => ({ ...s })),
      createdAt: new Date().toISOString(),
    };
    setSavedPresets(prev => [preset, ...prev]);
    setActivePresetId(preset.id);
  }, [editableScenes]);

  const loadPreset = useCallback((id: string) => {
    const preset = savedPresets.find(p => p.id === id);
    if (preset) {
      setEditableScenes(preset.scenes.map(s => ({ ...s })));
      setActivePresetId(id);
    }
  }, [savedPresets]);

  const deletePreset = useCallback((id: string) => {
    setSavedPresets(prev => prev.filter(p => p.id !== id));
    if (activePresetId === id) setActivePresetId(null);
  }, [activePresetId]);

  const currentScene = editableScenes[state.currentSceneIndex] || editableScenes[0];

  return (
    <CinematicContext.Provider value={{
      state, currentScene, scenes: editableScenes,
      startCinematic, stopCinematic, nextScene, prevScene, goToScene,
      replayScene, toggleNarrator, markSceneComplete, updateScene, resetScenes,
      savedPresets, savePreset, loadPreset, deletePreset, activePresetId,
    }}>
      {children}
    </CinematicContext.Provider>
  );
}

export function useCinematic() {
  const ctx = useContext(CinematicContext);
  if (!ctx) throw new Error("useCinematic must be used within CinematicProvider");
  return ctx;
}
